import { QueryClient, QueryClientProvider } from "react-query";
import { Outlet, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/auth.context";
import { Toaster } from "sonner";
import { Suspense, lazy } from "react";
import { AnimatePresence } from "framer-motion";

// Enterprise-grade QueryClient configuration for 1k+ users
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache configuration for high performance
      cacheTime: 5 * 60 * 1000, // 5 minutes cache time
      staleTime: 2 * 60 * 1000, // 2 minutes stale time
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: (failureCount, error: any) => {
        // Retry logic for different error types
        if (error?.response?.status === 404) return false;
        if (error?.response?.status === 401) return false;
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Lazy load components for better performance
const LazyDashboard = lazy(() => import("./pages/Dashboard"));
const LazyContributions = lazy(() => import("./pages/Contributions"));
const LazyLoans = lazy(() => import("./pages/Loans"));
const LazyExpenses = lazy(() => import("./pages/Expenses"));
const LazySystemUsers = lazy(() => import("./pages/SystemUsers"));

function App() {
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#4dbd07]"></div>
            </div>
          }>
            <Outlet />
          </Suspense>
          <Toaster
            toastOptions={{
              classNames: {
                error: "!bg-red-50 !text-red-400 border-red-500",
                info: "bg-blue-400",
                success: "!bg-green-50 !text-green-400 !border-green-500",
                warning: "bg-orange-400 border-green-500",
                toast: "bg-blue-400",
                title: "text-sm font-sans",
                actionButton: "bg-zinc-400",
                cancelButton: "bg-orange-400",
                closeButton: "bg-lime-400",
              },
            }}
          />
        </AuthProvider>
      </QueryClientProvider>
    </>
  );
}

export default App;
